/**
 * @fileoverview Tax calculation utilities for French income tax system
 * 
 * This module provides comprehensive tax calculation functions using the French
 * progressive tax system with 2024 tax brackets. It calculates income tax
 * based on the official French tax rates and provides detailed breakdowns.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2024
 */

import { TAX_BRACKETS_2024 } from '../constants/business';

/**
 * Represents the tax calculation for a specific tax bracket
 * 
 * @interface TaxBracketBreakdown
 * @property {string} bracket - Description of the tax bracket range
 * @property {number} rate - Tax rate percentage for this bracket
 * @property {number} amount - Tax amount calculated for this bracket
 */
export interface TaxBracketBreakdown {
  bracket: string;
  rate: number;
  amount: number;
}

/**
 * Complete tax calculation result with breakdown by brackets
 * 
 * @interface TaxCalculation
 * @property {number} tax - Total tax amount in euros
 * @property {number} effectiveRate - Effective tax rate as percentage
 * @property {TaxBracketBreakdown[]} breakdown - Detailed breakdown by tax brackets
 */
export interface TaxCalculation {
  tax: number;
  effectiveRate: number;
  breakdown: TaxBracketBreakdown[];
}

/**
 * Calculates French progressive income tax with detailed breakdown
 * 
 * This function implements the French progressive tax system using the 2024 tax brackets.
 * It calculates the tax amount for each applicable bracket and provides a complete
 * breakdown showing how the total tax is computed.
 * 
 * The French tax system applies different rates to different income ranges:
 * - 0% up to 11,497€
 * - 11% from 11,498€ to 29,315€
 * - 30% from 29,316€ to 83,823€
 * - 41% from 83,824€ to 180,294€
 * - 45% above 180,294€
 * 
 * @param {number} income - Annual taxable income in euros
 * @returns {TaxCalculation} Complete tax calculation with breakdown
 * 
 * @example
 * // Calculate tax for 35,000€ annual income
 * const taxCalc = calculateProgressiveTax(35000);
 * console.log(taxCalc.tax); // 3,258€
 * console.log(taxCalc.effectiveRate); // 9.3%
 * console.log(taxCalc.breakdown); // Detailed breakdown by brackets
 * 
 * @example
 * // Tax calculation breakdown for 35,000€:
 * // - 0€ to 11,497€: 0% = 0€
 * // - 11,498€ to 29,315€: 11% = 1,960€
 * // - 29,316€ to 35,000€: 30% = 1,705€
 * // Total: 3,665€
 * 
 * @see {@link https://www.impots.gouv.fr/particulier/bareme-de-limpot-sur-le-revenu} Official tax brackets
 */
export function calculateProgressiveTax(income: number): TaxCalculation {
  if (income <= 0) {
    return {
      tax: 0,
      effectiveRate: 0,
      breakdown: []
    };
  }

  let totalTax = 0;
  const breakdown: TaxBracketBreakdown[] = [];

  for (const bracket of TAX_BRACKETS_2024) {
    if (income <= bracket.min) break;

    const taxableInThisBracket = Math.min(income, bracket.max) - bracket.min;
    const taxForThisBracket = taxableInThisBracket * (bracket.rate / 100);
    
    if (taxForThisBracket > 0) {
      totalTax += taxForThisBracket;
      
      const bracketLabel = bracket.max === Infinity 
        ? `Plus de ${bracket.min.toLocaleString('fr-FR')} €`
        : `${bracket.min.toLocaleString('fr-FR')} € - ${bracket.max.toLocaleString('fr-FR')} €`;
      
      breakdown.push({
        bracket: bracketLabel,
        rate: bracket.rate,
        amount: taxForThisBracket
      });
    }
  }

  const effectiveRate = (totalTax / income) * 100;

  return {
    tax: Math.round(totalTax),
    effectiveRate,
    breakdown
  };
}